import { useRef, useState } from 'react';
import IValidatable from '../../../../Shared/validatable.interface';
import ValidationInfo from '../../../../../validation/validation-info';
import HoneyPot, { IHoneyPot } from '../../../../Shared/HoneyPot';
import Consent, { IConsentState } from '../../../../Shared/Consent';
import { ValidatableComponent, ValidationHelper } from '../../../../../helpers/validation.helper';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { IServerValidationError } from '../../../../Shared/validatable.interface';
import { TranslationService } from '../../../../../services/translation.service';
import { Status } from '../../../../../hooks/fetch.hook';
import { flushSync } from 'react-dom';
import type { PrivateIndividualRegistrationApiModel } from './models/private-individual-registration.api-model';
import SelectFormGroup from '../../../../Shared/SelectFormGroup';
import ValidationSummary from '../../../../Shared/ValidationSummary';
import PersonalIdentityNumberLookup from '../../../../Shared/PersonalIdentityNumberLookup';
import TextFieldFormGroup from '../../../../Shared/TextFieldFormGroup';
import IContactInformation, { emptyContactInformation } from '../../../../../models/contact-information.model';
import AntiforgeryService from '../../../../../services/anti-forgery.service';
import { SubmitState, SubmitSource } from '../models/submit-state.model';
import Alert from '../../../../Shared/Alert';
import { PersonalIdentityNumberFeature } from '../../../../Shared/PersonalIdentityNumberLookup/personal-identity-lookup-feature.enum';

const subscribeReasons = [
  { value: 'workingwithpublictransport', text: 'Jag jobbar med kollektivtrafik och vill hålla mig uppdaterad.' },
  { value: 'interestedincontent', text: 'Jag jobbar inte med kollektivtrafik, men är intresserad av tidningens innehåll.' },
  { value: 'noanswer', text: 'Vill inte svara.' },
];

function PrivateIndividualRegistrationForm({ onSubmitSuccessful }: { onSubmitSuccessful: (state: SubmitState) => void }) {
  // === STATE ===
  const [personalIdentityNumberLookupState, setPersonalIdentityNumberLookupState] = useState<{
    value: IContactInformation;
    validationInfo: ValidationInfo;
  }>({
    value: emptyContactInformation,
    validationInfo: new ValidationInfo(),
  });
  const [reasonState, setReasonState] = useState<{ value: string; validationInfo: ValidationInfo }>({
    value: '',
    validationInfo: new ValidationInfo(),
  });

  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [emailState, setEmailState] = useState<{ value: string; validationInfo: ValidationInfo }>({
    value: '',
    validationInfo: new ValidationInfo(),
  });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // === REFS ===
  const personalIdentityNumberLookupRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);
  const reasonRef = useRef<IValidatable>(null!);
  const consentRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);

  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: personalIdentityNumberLookupRef.current, validationInfo: personalIdentityNumberLookupState.validationInfo, setState: setPersonalIdentityNumberLookupState },
      { component: emailRef.current, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: reasonRef.current, validationInfo: reasonState.validationInfo, setState: setReasonState },
      { component: consentRef.current, validationInfo: consentState.validationInfo, setState: setConsentState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);

      if (!honeyPotRef.current.isValid()) return;

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      const application: PrivateIndividualRegistrationApiModel = {
        personalIdentityNumber: personalIdentityNumberLookupState.value.personalIdentityNumber,
        email: emailState.value,
        reason: reasonState.value,
        isConsentGiven: consentState.value,
      };

      try {
        setSubmitStatus(Status.loading);
        const response = await fetch('/api/stralkastaren/registration/private-individual', {
          method: 'POST',
          body: JSON.stringify(application),
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          onSubmitSuccessful({ isSubmitted: true, status: Status.success, errorCode: undefined, source: SubmitSource.Private });
          AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, 'Skickat registrering - privatperson', subscribeReasons.find((r) => r.value === reasonState.value)?.text || '');
        } else {
          setSubmitStatus(Status.failed);
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
            const validationSummary = document.getElementById('private-individual-registration-form-validation-summary');
            if (validationSummary) {
              validationSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
              validationSummary.focus();
            }
          } else {
            setShowErrorAlert(true);
            AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, 'Skickat registrering - privatperson - misslyckat', '');
          }
        }
      } catch {
        setSubmitStatus(Status.failed);
        setShowErrorAlert(true);
      }
    });
  }

  return (
    <div>
      <div className="private-individual-registration-form">
        <ValidationSummary id="private-individual-registration-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
        <PersonalIdentityNumberLookup
          ref={personalIdentityNumberLookupRef}
          state={personalIdentityNumberLookupState}
          hideRequiredAsterisk={true}
          feature={PersonalIdentityNumberFeature.StralkastarenRegistration}
          onChange={(value) => {
            setPersonalIdentityNumberLookupState((ps) => ({ ...ps, value }));
            setPersonalIdentityNumberLookupState((ps) => ({
              ...ps,
              validationInfo: new ValidationInfo('', [], {
                ...ps.validationInfo.children,
                personalIdentityNumber: new ValidationInfo(),
              }),
            }));
          }}
        />
        <TextFieldFormGroup
          ref={emailRef}
          required={true}
          labelText={TranslationService.get('common.email')}
          inputId="personal-identity-email"
          serverValidationName="email"
          type="email"
          hideRequiredAsterisk={true}
          state={emailState}
          autocomplete="off"
          onChange={(value) => {
            setEmailState((ps) => ({ ...ps, value, validationInfo: new ValidationInfo() }));
          }}
        />
        <SelectFormGroup
          ref={reasonRef}
          labelText="Anledning"
          id="reason"
          serverValidationName="reason"
          state={reasonState}
          placeholder="Välj anledning"
          required={true}
          hideRequiredAsterisk={true}
          options={subscribeReasons}
          onChange={(value: string) => setReasonState((ps) => ({ ...ps, value, validationInfo: new ValidationInfo() }))}
        />
        <Consent
          ref={consentRef}
          id="private-individual-registration-consent"
          consentText={TranslationService.get('stralkastarenregistrationpage.consenttext')}
          integrityPolicyInfo={TranslationService.get('stralkastarenregistrationpage.integritypolicyinfo')}
          termsAndConditions={TranslationService.get('stralkastarenregistrationpage.termsandconditionsheading')}
          className="mb-4"
          state={consentState}
          onChange={(value) => {
            setConsentState({ value, validationInfo: new ValidationInfo() });
          }}
        />
        <HoneyPot ref={honeyPotRef} />
        <div className={`d-grid ${showErrorAlert ? 'mb-3' : ''}`}>
          <button type="button" className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick}>
            <span hidden={submitStatus === Status.loading}>Prenumerera</span>
          </button>
        </div>

        <Alert
          show={showErrorAlert}
          heading={TranslationService.get('stralkastarenregistrationpage.failedsubmissionheadingtext')}
          headingTag="h2"
          content={TranslationService.get('stralkastarenregistrationpage.failedsubmissionbodytext')}
          closable={false}
        />
      </div>
    </div>
  );
}

export default PrivateIndividualRegistrationForm;